import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Lock, LogOut, Mail, Building2, RefreshCw, ArrowLeft, Loader2, Inbox, BarChart3, CornerUpLeft, FileDown, Search, CheckCheck, Bell, BellOff } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from "recharts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "ap_admin_token";
const READ_KEY = "ap_read_messages";
const POLL_MS = 30000;
const CHART_COLORS = ["#00E5FF", "#38BDF8", "#818CF8", "#22D3EE", "#5EEAD4"];

const SHORT_LABELS = {
  "consultant": "Consultant",
  "chef-projet": "Chef de projet",
  "business-analyst": "Business Analyst",
  "business-dev": "Business Dev",
  "chef-produit": "Chef de produit",
};

const CvTooltip = ({ active, payload }) => {
  if (!active || !payload || !payload.length) return null;
  const d = payload[0].payload;
  return (
    <div className="bg-[#0B101A] border border-cyan/40 rounded-xl px-3.5 py-2.5 shadow-xl" data-testid="admin-cv-tooltip">
      <p className="text-xs text-[var(--text-2)] max-w-[200px] leading-snug">{d.full}</p>
      <p className="text-sm text-cyan font-medium mt-1">{d.value} téléchargement{d.value > 1 ? "s" : ""}</p>
    </div>
  );
};

function formatApiErrorDetail(detail) {
  if (detail == null) return "Une erreur est survenue. Réessayez.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  if (detail?.msg) return detail.msg;
  return String(detail);
}

const LoginView = ({ onLogin }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem(TOKEN_KEY, data.token);
      onLogin(data.token);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md bg-[#111827] border border-white/15 rounded-2xl p-8 md:p-10 shadow-2xl shadow-black/50"
      >
        <div className="flex items-center gap-3 mb-8">
          <span className="h-10 w-10 inline-flex items-center justify-center rounded-full bg-cyan/10 border border-cyan/30 text-cyan"><Lock size={18} /></span>
          <div>
            <h1 className="font-display font-medium text-xl">Espace privé</h1>
            <p className="text-sm text-[var(--text-2)]">Messages reçus</p>
          </div>
        </div>
        <form onSubmit={submit} className="space-y-6" data-testid="admin-login-form">
          <div>
            <label className="block text-xs uppercase tracking-[0.2em] text-white/70 mb-3">E-mail</label>
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
              className="w-full bg-transparent border-b border-white/20 py-2.5 text-white focus:border-cyan focus:outline-none transition-colors"
              data-testid="admin-email" />
          </div>
          <div>
            <label className="block text-xs uppercase tracking-[0.2em] text-white/70 mb-3">Mot de passe</label>
            <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required
              className="w-full bg-transparent border-b border-white/20 py-2.5 text-white focus:border-cyan focus:outline-none transition-colors"
              data-testid="admin-password" />
          </div>
          {error && <p className="text-sm text-red-400" data-testid="admin-error">{error}</p>}
          <button type="submit" disabled={loading}
            className="w-full inline-flex items-center justify-center gap-2 bg-cyan text-[#05070A] font-medium px-6 py-3.5 rounded-full hover:bg-white transition-colors disabled:opacity-60"
            data-testid="admin-login-btn">
            {loading ? <><Loader2 size={18} className="animate-spin" /> Connexion…</> : "Se connecter"}
          </button>
        </form>
        <Link to="/" className="mt-8 inline-flex items-center gap-2 text-sm text-[var(--text-2)] hover:text-cyan transition-colors" data-testid="admin-back-home">
          <ArrowLeft size={15} /> Retour au portfolio
        </Link>
      </motion.div>
    </div>
  );
};

const Dashboard = ({ token, onLogout }) => {
  const [messages, setMessages] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const [readIds, setReadIds] = useState(() => {
    try { return JSON.parse(localStorage.getItem(READ_KEY) || "[]"); } catch { return []; }
  });
  const [notifyEnabled, setNotifyEnabled] = useState(
    () => typeof Notification !== "undefined" && Notification.permission === "granted"
  );
  const [newAlert, setNewAlert] = useState(0);
  const prevCountRef = useRef(null);

  const persistRead = (ids) => {
    setReadIds(ids);
    localStorage.setItem(READ_KEY, JSON.stringify(ids));
  };
  const markRead = (id) => { if (id && !readIds.includes(id)) persistRead([...readIds, id]); };
  const markAllRead = () => persistRead(messages.map((m) => m.id));
  const isUnread = (m) => !readIds.includes(m.id);
  const openMessage = (m) => { setSelectedId(m.id); markRead(m.id); };

  const enableNotifications = async () => {
    if (typeof Notification === "undefined") return;
    if (Notification.permission === "granted") { setNotifyEnabled(true); return; }
    const perm = await Notification.requestPermission();
    setNotifyEnabled(perm === "granted");
  };

  const handleIncoming = useCallback((newMessages) => {
    const prev = prevCountRef.current;
    if (prev !== null && newMessages.length > prev) {
      const diff = newMessages.length - prev;
      setNewAlert((n) => n + diff);
      const latest = newMessages[0];
      if (typeof Notification !== "undefined" && Notification.permission === "granted") {
        try {
          new Notification(diff > 1 ? `${diff} nouveaux messages` : "Nouveau message reçu", {
            body: latest ? `${latest.nom}${latest.entreprise ? " · " + latest.entreprise : ""} — ${latest.objet}` : "",
            tag: "ap-new-message",
          });
        } catch (e) { /* noop */ }
      }
    }
    prevCountRef.current = newMessages.length;
  }, []);

  const fetchData = useCallback(async (silent = false) => {
    if (!silent) { setLoading(true); setError(""); }
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [msgRes, statsRes] = await Promise.all([
        axios.get(`${API}/admin/messages`, { headers }),
        axios.get(`${API}/admin/cv-stats`, { headers }),
      ]);
      handleIncoming(msgRes.data);
      setMessages(msgRes.data);
      setStats(statsRes.data);
    } catch (err) {
      if (err.response?.status === 401) { onLogout(); return; }
      if (!silent) setError("Impossible de charger les données.");
    } finally {
      if (!silent) setLoading(false);
    }
  }, [token, onLogout, handleIncoming]);

  const load = useCallback(() => fetchData(false), [fetchData]);

  useEffect(() => { fetchData(false); }, [fetchData]);

  // Rafraîchissement automatique (polling) pour l'alerte temps réel
  useEffect(() => {
    const id = setInterval(() => fetchData(true), POLL_MS);
    return () => clearInterval(id);
  }, [fetchData]);

  // Badge dans le titre de l'onglet
  useEffect(() => {
    const unread = messages.filter((m) => !readIds.includes(m.id)).length;
    document.title = unread > 0 ? `(${unread}) Espace privé — Andrew Picornell` : "Espace privé — Andrew Picornell";
    return () => { document.title = "Andrew Picornell"; };
  }, [messages, readIds]);

  const replyHref = (m) => {
    const subject = `RE : ${m.objet}`;
    const body =
      `Bonjour ${m.nom},\n\n` +
      `Merci pour votre message et l'intérêt porté à mon profil.\n\n` +
      `[Votre réponse ici]\n\n` +
      `Bien cordialement,\n` +
      `Andrew Picornell\n` +
      `Étudiant à IMT-BS — Management des écosystèmes à l'ère de l'IA\n` +
      `contact@andrewpicornell.fr`;
    return `mailto:${m.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  const exportCsv = () => {
    const headers = ["Date", "Nom", "E-mail", "Entreprise", "Objet", "Message"];
    const rows = messages.map((m) => [
      new Date(m.created_at).toLocaleString("fr-FR"),
      m.nom, m.email, m.entreprise || "", m.objet, m.message,
    ]);
    const esc = (v) => `"${String(v).replace(/"/g, '""')}"`;
    const csv = "\uFEFF" + [headers, ...rows].map((r) => r.map(esc).join(";")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `messages-portfolio-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const q = query.trim().toLowerCase();
  const filtered = q
    ? messages.filter((m) => [m.nom, m.email, m.entreprise, m.objet, m.message].some((v) => (v || "").toLowerCase().includes(q)))
    : messages;
  const unreadCount = messages.filter(isUnread).length;
  const selected = messages.find((m) => m.id === selectedId) || null;

  return (
    <div className="min-h-screen max-w-5xl mx-auto px-6 py-12">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-10">
        <div>
          <h1 className="font-display font-medium text-3xl">Espace privé</h1>
          <p className="text-[var(--text-2)] mt-1">
            {messages.length} message{messages.length > 1 ? "s" : ""} reçu{messages.length > 1 ? "s" : ""}
            {unreadCount > 0 && <span className="text-cyan"> · {unreadCount} non lu{unreadCount > 1 ? "s" : ""}</span>}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={notifyEnabled ? () => setNotifyEnabled(false) : enableNotifications}
            className={`inline-flex items-center gap-2 border rounded-full px-4 py-2 text-sm transition-colors ${notifyEnabled ? "border-cyan/50 text-cyan" : "border-white/15 hover:border-cyan/50"}`}
            data-testid="admin-notify-toggle"
            title={notifyEnabled ? "Notifications activées" : "Activer les notifications navigateur"}
          >
            {notifyEnabled ? <Bell size={15} /> : <BellOff size={15} />} {notifyEnabled ? "Alertes activées" : "Activer les alertes"}
          </button>
          <button onClick={exportCsv} disabled={messages.length === 0} className="inline-flex items-center gap-2 border border-white/15 rounded-full px-4 py-2 text-sm hover:border-cyan/50 transition-colors disabled:opacity-40" data-testid="admin-export-csv">
            <FileDown size={15} /> Exporter (CSV)
          </button>
          <button onClick={() => { setNewAlert(0); load(); }} className="inline-flex items-center gap-2 border border-white/15 rounded-full px-4 py-2 text-sm hover:border-cyan/50 transition-colors" data-testid="admin-refresh">
            <RefreshCw size={15} /> Actualiser
          </button>
          <button onClick={onLogout} className="inline-flex items-center gap-2 border border-white/15 rounded-full px-4 py-2 text-sm hover:border-red-500/50 hover:text-red-400 transition-colors" data-testid="admin-logout">
            <LogOut size={15} /> Déconnexion
          </button>
        </div>
      </div>

      {/* Alerte nouveau message (temps réel) */}
      {newAlert > 0 && (
        <motion.button
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          onClick={() => { setNewAlert(0); setQuery(""); setSelectedId(null); }}
          className="w-full mb-8 flex items-center gap-3 bg-cyan/10 border border-cyan/40 rounded-2xl px-5 py-3.5 text-left hover:bg-cyan/15 transition-colors"
          data-testid="admin-new-alert"
        >
          <span className="relative flex h-2.5 w-2.5 shrink-0">
            <span className="absolute inline-flex h-full w-full rounded-full bg-cyan opacity-75 animate-ping" />
            <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-cyan" />
          </span>
          <span className="text-sm text-white">
            {(newAlert > 1 ? `${newAlert} nouveaux messages viennent d'arriver` : "Un nouveau message vient d'arriver") + " — cliquez pour l'afficher."}
          </span>
        </motion.button>
      )}

      {/* Statistiques de téléchargement des CV */}
      {stats && (
        <div className="mb-12" data-testid="admin-cv-stats">
          <div className="flex items-center gap-2 mb-5">
            <BarChart3 size={16} className="text-cyan" />
            <h2 className="text-xs uppercase tracking-[0.2em] text-white/70">CV téléchargés par poste</h2>
            <span className="text-xs text-[var(--muted)]">· {stats.total} au total</span>
          </div>
          <div className="bg-surface border border-white/5 rounded-2xl p-5 md:p-6">
            {stats.total === 0 ? (
              <p className="text-sm text-[var(--text-2)] py-8 text-center" data-testid="admin-cv-stats-empty">
                Aucun téléchargement de CV pour le moment.
              </p>
            ) : (
              <div className="h-64 w-full" data-testid="admin-cv-chart">
                <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={240}>
                  <BarChart
                    data={stats.stats.map((s) => ({ name: SHORT_LABELS[s.slug] || s.label, full: s.label, value: s.count }))}
                    margin={{ top: 8, right: 8, left: -18, bottom: 8 }}
                  >
                    <XAxis dataKey="name" tick={{ fill: "#94A3B8", fontSize: 11 }} tickLine={false} axisLine={{ stroke: "#1e2a3a" }} interval={0} />
                    <YAxis allowDecimals={false} tick={{ fill: "#64748B", fontSize: 11 }} tickLine={false} axisLine={false} width={40} />
                    <Tooltip cursor={{ fill: "rgba(0,229,255,0.06)" }} content={<CvTooltip />} />
                    <Bar dataKey="value" radius={[6, 6, 0, 0]} maxBarSize={72}>
                      {stats.stats.map((s, i) => (
                        <Cell key={s.slug} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
            {/* Légende / détail chiffré */}
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mt-5 pt-5 border-t border-white/5">
              {stats.stats.map((s, i) => (
                <div key={s.slug} className="flex items-start gap-2.5" data-testid={`admin-cv-stat-${s.slug}`}>
                  <span className="mt-1 h-2.5 w-2.5 rounded-sm shrink-0" style={{ background: CHART_COLORS[i % CHART_COLORS.length] }} />
                  <div className="min-w-0">
                    <p className="text-lg font-display font-medium text-white leading-none">{s.count}</p>
                    <p className="text-xs text-[var(--text-2)] mt-1 leading-snug">{s.label}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <h2 className="font-display font-medium text-xl">Messages reçus</h2>
        <div className="flex items-center gap-3 flex-1 sm:flex-none justify-end">
          <div className="relative flex-1 sm:w-72">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--muted)]" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Rechercher (nom, entreprise, mot-clé)…"
              className="w-full bg-surface border border-white/10 rounded-full pl-9 pr-3 py-2 text-sm text-white placeholder-[var(--muted)] focus:border-cyan/50 focus:outline-none transition-colors"
              data-testid="admin-search"
            />
          </div>
          {unreadCount > 0 && (
            <button onClick={markAllRead} className="inline-flex items-center gap-2 border border-white/15 rounded-full px-4 py-2 text-sm hover:border-cyan/50 transition-colors whitespace-nowrap" data-testid="admin-mark-all-read">
              <CheckCheck size={15} /> Tout marquer comme lu
            </button>
          )}
        </div>
      </div>
      {loading ? (
        <div className="flex items-center gap-3 text-[var(--text-2)]"><Loader2 size={18} className="animate-spin" /> Chargement…</div>
      ) : error ? (
        <p className="text-red-400">{error}</p>
      ) : messages.length === 0 ? (
        <div className="flex flex-col items-center justify-center text-center py-24 text-[var(--text-2)]" data-testid="admin-empty">
          <Inbox size={40} className="mb-4 text-[var(--muted)]" />
          Aucun message pour le moment.
        </div>
      ) : filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center text-center py-16 text-[var(--text-2)]" data-testid="admin-no-results">
          <Search size={32} className="mb-4 text-[var(--muted)]" />
          Aucun résultat pour « {query} ».
        </div>
      ) : (
        <div className="grid lg:grid-cols-12 border border-white/5 rounded-2xl overflow-hidden bg-surface">
          {/* Liste — style boîte mail */}
          <div className={`lg:col-span-5 lg:border-r border-white/5 divide-y divide-white/5 lg:max-h-[65vh] overflow-y-auto ${selected ? "hidden lg:block" : "block"}`} data-testid="admin-messages-list">
            {filtered.map((m, i) => {
              const unread = isUnread(m);
              const active = selected && selected.id === m.id;
              return (
                <button
                  key={m.id || i}
                  onClick={() => openMessage(m)}
                  className={`w-full text-left px-4 py-3.5 flex gap-3 transition-colors ${active ? "bg-cyan/5" : "hover:bg-white/[0.03]"}`}
                  data-testid={`admin-message-${i}`}
                  data-unread={unread}
                >
                  <span className="pt-1.5 shrink-0">
                    {unread
                      ? <span className="block h-2 w-2 rounded-full bg-cyan" title="Non lu" data-testid={`admin-unread-dot-${i}`} />
                      : <span className="block h-2 w-2 rounded-full bg-transparent" />}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="flex items-center justify-between gap-2">
                      <span className={`truncate text-sm ${unread ? "text-white font-medium" : "text-white/75"}`}>{m.nom}{m.entreprise ? ` · ${m.entreprise}` : ""}</span>
                      <span className="text-[11px] text-[var(--muted)] shrink-0">{new Date(m.created_at).toLocaleDateString("fr-FR", { day: "2-digit", month: "short" })}</span>
                    </span>
                    <span className={`block truncate text-sm mt-0.5 ${unread ? "text-white/90" : "text-[var(--text-2)]"}`}>{m.objet}</span>
                    <span className="block truncate text-xs text-[var(--muted)] mt-0.5">{m.message}</span>
                  </span>
                </button>
              );
            })}
          </div>

          {/* Détail du message sélectionné */}
          <div className={`lg:col-span-7 ${selected ? "block" : "hidden lg:block"}`} data-testid="admin-message-detail">
            {selected ? (
              <div className="p-6 md:p-8 lg:max-h-[65vh] overflow-y-auto">
                <button onClick={() => setSelectedId(null)} className="lg:hidden inline-flex items-center gap-2 text-sm text-[var(--text-2)] hover:text-cyan mb-5" data-testid="admin-detail-back">
                  <ArrowLeft size={15} /> Retour à la liste
                </button>
                <div className="flex items-start justify-between gap-3 mb-2">
                  <h3 className="font-display font-medium text-2xl leading-tight">{selected.objet}</h3>
                  <span className="text-xs text-[var(--muted)] shrink-0 mt-1">{new Date(selected.created_at).toLocaleString("fr-FR")}</span>
                </div>
                <div className="flex flex-wrap gap-4 text-sm text-[var(--text-2)] mb-6 border-b border-white/5 pb-5">
                  <span className="text-white/90">{selected.nom}</span>
                  <a href={`mailto:${selected.email}`} className="inline-flex items-center gap-1.5 hover:text-cyan"><Mail size={14} /> {selected.email}</a>
                  {selected.entreprise && <span className="inline-flex items-center gap-1.5"><Building2 size={14} /> {selected.entreprise}</span>}
                </div>
                <p className="text-white/85 leading-relaxed whitespace-pre-line">{selected.message}</p>
                <div className="mt-8">
                  <a href={replyHref(selected)} onClick={() => markRead(selected.id)} rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-cyan text-[#05070A] font-medium text-sm px-5 py-2.5 rounded-full hover:bg-white transition-colors" data-testid="admin-detail-reply">
                    <CornerUpLeft size={15} /> Répondre
                  </a>
                </div>
              </div>
            ) : (
              <div className="hidden lg:flex flex-col items-center justify-center text-center min-h-[320px] h-full text-[var(--text-2)] p-8" data-testid="admin-detail-empty">
                <Mail size={36} className="mb-4 text-[var(--muted)]" />
                Sélectionnez un message pour le lire.
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default function AdminPage() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
  }, []);

  return (
    <div className="bg-[#05070A] text-white antialiased min-h-screen">
      {token ? <Dashboard token={token} onLogout={logout} /> : <LoginView onLogin={setToken} />}
    </div>
  );
}
